import React, { useEffect, useState, useCallback } from 'react';

import { GlobalLoader } from '@shared/components';
import { Help } from '@mui/icons-material';
import { Tooltip as MUITooltip } from '@mui/material';
import { useOrgEmployeeApi } from '@shared/services';
import { useI18n } from '@shared/i18n';
import type { Employee, OrgEmployee, RestList } from '@shared/types';
import { DepartmentTabs } from './components';
import styles from './MyDepartments.module.css';

const MyDepartments: React.FC = (): React.ReactElement => {
    const { t } = useI18n();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [departmentEmployees, setDepartmentEmployees] = useState<
        RestList<Employee> | undefined
    >();
    const [selectedDepartmentEmployee, setSelectedDepartmentEmployee] =
        useState<Employee | undefined>();
    const [orgEmployee, setOrgEmployee] = useState<OrgEmployee | undefined>();
    const [activeDepartmentTab, setActiveDepartmentTab] = useState<string>('');

    const { get: getOrgEmployee, getEmployees } = useOrgEmployeeApi();

    const getData = useCallback(async (): Promise<void> => {
        try {
            const oe = await getOrgEmployee();
            if (oe) {
                setOrgEmployee(oe);
                if (oe.employees.length > 0) {
                    setActiveDepartmentTab(oe.employees[0].id);
                }
            }
            const employees = await getEmployees();
            if (employees) {
                setDepartmentEmployees(employees);
                setSelectedDepartmentEmployee(employees.elements[0]);
            }
        } catch (err) {
            console.error('MyDepartments getData error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [getOrgEmployee, getEmployees]);

    useEffect((): void => {
        getData().catch(console.error);
    }, [getData]);

    // Update selected employee when department tab changes
    useEffect((): void => {
        if (activeDepartmentTab && departmentEmployees) {
            const employee = departmentEmployees.elements.find(
                (empl) => empl.id === activeDepartmentTab,
            );
            if (employee) {
                setSelectedDepartmentEmployee(employee);
            }
        }
    }, [activeDepartmentTab, departmentEmployees]);

    return (
        <div className={styles.container}>
            {isLoading && <GlobalLoader className={styles.loadingSpinner} />}
            {!isLoading && orgEmployee && departmentEmployees && (
                <>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            {t('departments.pageTitle')}
                        </h1>
                        <MUITooltip
                            arrow
                            title={
                                <div className={styles.tooltipContent}>
                                    <strong>
                                        {t('departments.tooltip.title')}
                                    </strong>
                                    <p className={styles.tooltipText}>
                                        {t('departments.tooltip.description')}
                                    </p>
                                </div>
                            }
                        >
                            <Help className={styles.helpIcon} />
                        </MUITooltip>
                    </div>

                    <DepartmentTabs
                        orgEmployee={orgEmployee}
                        selectedDepartmentEmployee={selectedDepartmentEmployee}
                        activeDepartmentTab={activeDepartmentTab}
                        setActiveDepartmentTab={setActiveDepartmentTab}
                    />
                </>
            )}
            {!isLoading && (!orgEmployee || !departmentEmployees) && (
                <p className={styles.errorMessage}>
                    {t('departments.loadError')}
                </p>
            )}
        </div>
    );
};

export default MyDepartments;
